import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import { Toast as RToast } from 'radix-ui';
import { useSyncExternalStore, type ReactNode } from 'react';
import {
  createToastStore,
  type ToastInput,
  type ToastRecord,
  type ToastStore,
  type ToastTone,
} from '../lib/toast-store.js';
import './toast.css';

const defaultStore = createToastStore();

/**
 * Shows a toast. Returns its id.
 *   toast({ title: 'Appointment moved', action: { label: 'Undo', onAction } })
 */
export function toast(input: ToastInput, store: ToastStore = defaultStore): string {
  return store.show(input);
}

toast.dismiss = (id?: string, store: ToastStore = defaultStore) => store.dismiss(id);

/** Convenience for optimistic or destructive actions: a 6 s toast with Undo. */
toast.undoable = (title: string, onUndo: () => void, store: ToastStore = defaultStore) =>
  store.show({ title, action: { label: 'Undo', onAction: onUndo } });

const ICONS: Record<ToastTone, ReactNode> = {
  neutral: null,
  success: <CircleCheck size={16} aria-hidden />,
  warning: <TriangleAlert size={16} aria-hidden />,
  danger: <CircleAlert size={16} aria-hidden />,
  info: <Info size={16} aria-hidden />,
};

function ToastItem({ t, store }: { t: ToastRecord; store: ToastStore }) {
  return (
    <RToast.Root
      className="vel-toast"
      data-tone={t.tone}
      duration={Number.isFinite(t.duration) ? t.duration : 1e9}
      type={t.tone === 'danger' ? 'foreground' : 'background'}
      onOpenChange={(open) => {
        if (!open) store.dismiss(t.id);
      }}
    >
      {ICONS[t.tone] ? <span className="vel-toast__icon">{ICONS[t.tone]}</span> : null}
      <div className="vel-toast__text">
        <RToast.Title className="vel-toast__title">{t.title}</RToast.Title>
        {t.description ? (
          <RToast.Description className="vel-toast__desc">{t.description}</RToast.Description>
        ) : null}
      </div>
      {t.action ? (
        <RToast.Action
          altText={`${t.action.label} (also available from the activity history)`}
          className="vel-toast__action"
          onClick={() => {
            t.action?.onAction();
            store.dismiss(t.id);
          }}
        >
          {t.action.label}
        </RToast.Action>
      ) : null}
      <RToast.Close className="vel-toast__close" aria-label="Dismiss">
        <X size={14} aria-hidden />
      </RToast.Close>
    </RToast.Root>
  );
}

export interface ToastHostProps {
  /** Inject a store for tests or isolated surfaces; defaults to the global one. */
  store?: ToastStore;
}

/** Mount once near the root. Bottom-left, 6 s default (BUILD-PLAN 3.3). */
export function ToastHost({ store = defaultStore }: ToastHostProps) {
  const toasts = useSyncExternalStore(store.subscribe, store.get, store.get);
  return (
    <RToast.Provider swipeDirection="left" label="Notifications">
      {toasts.map((t) => (
        <ToastItem key={t.id} t={t} store={store} />
      ))}
      <RToast.Viewport className="vel-toast-viewport" />
    </RToast.Provider>
  );
}
